import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, 
  IconButton, Chip, Drawer, TextField, FormControl, InputLabel, Select, MenuItem, 
  LinearProgress, Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function KPIs() {
  const [kpis, setKpis] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentKPI, setCurrentKPI] = useState({ 
    user_id: '', project_id: '', name: '', description: '', 
    value: 0, target: 100, unit: 'Count', status: 'Pending', period: '' 
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [kRes, uRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/kpis`, { headers }),
        axios.get(`${API_URL}/setup/users`, { headers }),
        axios.get(`${API_URL}/projects`, { headers })
      ]);
      setKpis(kRes.data);
      setUsers(uRes.data);
      setProjects(pRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      const payload = {
        ...currentKPI,
        project_id: currentKPI.project_id || null
      };
      
      if (editMode) {
        await axios.put(`${API_URL}/kpis/${currentKPI.id}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/kpis`, payload, { headers });
      }
      setOpenDrawer(false);
      fetchData();
    } catch (err) {
      alert('Error saving KPI');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (kpi) => {
    setCurrentKPI(kpi);
    setEditMode(true);
    setOpenDrawer(true);
  };

  const handleAdd = () => {
    setCurrentKPI({ 
      user_id: '', project_id: '', name: '', description: '', 
      value: 0, target: 100, unit: 'Count', status: 'Pending', period: '' 
    });
    setEditMode(false);
    setOpenDrawer(true);
  };

  const handleDelete = async (id) => {
    if(!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API_URL}/kpis/${id}`, { headers });
      fetchData();
    } catch (err) {
      alert('Error deleting KPI');
    }
  };

  const getProgressColor = (value, target) => {
    const percentage = (value / target) * 100;
    if (percentage >= 100) return 'success';
    if (percentage >= 70) return 'primary';
    return 'warning';
  };

  if (loading) return <Loading message="Loading KPIs..." />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon color="primary" /> Employee KPIs
        </Typography>
        <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleAdd}
            sx={{ borderRadius: 2, px: 3 }}
        >
            Add KPI
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Table>
            <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>KPI Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
            {kpis.map(k => (
                <TableRow key={k.id} hover>
                    <TableCell>
                        <Typography fontWeight="medium">{k.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{k.unit}</Typography>
                    </TableCell>
                    <TableCell>{k.user_name}</TableCell>
                    <TableCell>{k.project_name || '-'}</TableCell>
                    <TableCell sx={{ width: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={k.target > 0 ? Math.min((k.value / k.target) * 100, 100) : 0} 
                                    color={getProgressColor(k.value, k.target)}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>
                            <Typography variant="caption" fontWeight="bold">
                                {k.value}/{k.target}
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Chip 
                            label={k.status} 
                            size="small" 
                            color={k.status === 'Achieved' ? 'success' : k.status === 'Failed' ? 'error' : 'default'} 
                            variant={k.status === 'Pending' ? 'outlined' : 'filled'}
                        />
                    </TableCell>
                    <TableCell>{k.period}</TableCell>
                    <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEdit(k)} color="primary">
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(k.id)} color="error">
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </TableCell>
                </TableRow>
            ))}
            {kpis.length === 0 && (
                <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No KPIs found.
                    </TableCell>
                </TableRow>
            )}
            </TableBody>
        </Table>
      </Paper>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 450 }, p: 0 } }}
      >
          <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">{editMode ? 'Edit KPI' : 'New KPI'}</Typography>
              <IconButton onClick={() => setOpenDrawer(false)} sx={{ color: 'white' }}>
                  <CloseIcon />
              </IconButton>
          </Box>
          
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                  <InputLabel>Employee</InputLabel>
                  <Select 
                    value={currentKPI.user_id} 
                    label="Employee" 
                    onChange={e => setCurrentKPI({...currentKPI, user_id: e.target.value})}
                    disabled={editMode} 
                  >
                      {users.map(u => <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>)}
                  </Select>
              </FormControl>

              <FormControl fullWidth>
                  <InputLabel>Project (Optional)</InputLabel>
                  <Select 
                    value={currentKPI.project_id || ''} 
                    label="Project (Optional)" 
                    onChange={e => setCurrentKPI({...currentKPI, project_id: e.target.value})}
                  >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </Select>
              </FormControl>

              <TextField 
                label="KPI Name" 
                fullWidth 
                value={currentKPI.name} 
                onChange={e => setCurrentKPI({...currentKPI, name: e.target.value})} 
              />

              <TextField 
                label="Description" 
                fullWidth 
                multiline 
                rows={3} 
                value={currentKPI.description || ''} 
                onChange={e => setCurrentKPI({...currentKPI, description: e.target.value})} 
              />

              <Grid container spacing={2}>
                  <Grid item xs={6}>
                      <TextField 
                        label="Target Value" 
                        type="number" 
                        fullWidth 
                        value={currentKPI.target} 
                        onChange={e => setCurrentKPI({...currentKPI, target: e.target.value})} 
                      />
                  </Grid>
                  <Grid item xs={6}>
                      <TextField 
                        label="Current Value" 
                        type="number" 
                        fullWidth 
                        value={currentKPI.value} 
                        onChange={e => setCurrentKPI({...currentKPI, value: e.target.value})} 
                      />
                  </Grid>
              </Grid>

              <Grid container spacing={2}>
                  <Grid item xs={6}>
                      <FormControl fullWidth>
                          <InputLabel>Unit</InputLabel>
                          <Select 
                            value={currentKPI.unit || 'Count'} 
                            label="Unit" 
                            onChange={e => setCurrentKPI({...currentKPI, unit: e.target.value})}
                          >
                              <MenuItem value="Count">Count</MenuItem>
                              <MenuItem value="Percentage">Percentage</MenuItem>
                              <MenuItem value="Currency">Currency</MenuItem>
                              <MenuItem value="Hours">Hours</MenuItem>
                          </Select>
                      </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                      <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select 
                            value={currentKPI.status || 'Pending'} 
                            label="Status" 
                            onChange={e => setCurrentKPI({...currentKPI, status: e.target.value})}
                          >
                              <MenuItem value="Pending">Pending</MenuItem>
                              <MenuItem value="In Progress">In Progress</MenuItem>
                              <MenuItem value="Achieved">Achieved</MenuItem>
                              <MenuItem value="Failed">Failed</MenuItem>
                          </Select>
                      </FormControl>
                  </Grid>
              </Grid>

              <TextField 
                label="Period" 
                fullWidth 
                placeholder="e.g. Q1 2025" 
                value={currentKPI.period || ''} 
                onChange={e => setCurrentKPI({...currentKPI, period: e.target.value})} 
              />

              <Button variant="contained" size="large" onClick={handleSave} sx={{ mt: 2 }} disabled={saveLoading}>
                  {saveLoading ? 'Saving...' : (editMode ? 'Update KPI' : 'Create KPI')}
              </Button>
          </Box>
      </Drawer>
    </Box>
  );
}

export default KPIs;
